// Native Studio Inc 1 — host-side room actions, all direct Supabase writes under
// RLS (no new Edge Function). Cohost designation is gated by the host-only
// room_cohosts policies; markRoomLive by rooms_update_host.

import { supabase } from '@/lib/supabase';

export interface CohostEntry {
  memberId: string;
  displayName: string;
  username: string | null;
  avatarUrl: string | null;
}

/**
 * Native publishers (host/cohost) don't fire the ingress webhook, so the host
 * marks the room live on going on air. Host RLS (rooms_update_host); idempotent
 * (only flips a not-yet-live room, so started_at is stamped once).
 */
export async function markRoomLive(roomId: string): Promise<void> {
  const { error } = await supabase
    .from('rooms')
    .update({ status: 'live', started_at: new Date().toISOString() })
    .eq('id', roomId)
    .neq('status', 'live');
  if (error) throw error;
}

export async function listCohosts(roomId: string): Promise<CohostEntry[]> {
  const { data, error } = await supabase
    .from('room_cohosts')
    .select(
      'member_id, member:profiles!room_cohosts_member_id_fkey(display_name, username, avatar_url)',
    )
    .eq('room_id', roomId);
  if (error) throw error;
  return (data ?? []).map((r) => {
    const m = Array.isArray(r.member) ? (r.member[0] ?? null) : r.member;
    return {
      memberId: r.member_id,
      displayName: m?.display_name ?? m?.username ?? 'Member',
      username: m?.username ?? null,
      avatarUrl: m?.avatar_url ?? null,
    };
  });
}

/** Resolve an existing member by username (profiles is public-select) → designate. */
export async function designateCohost(
  roomId: string,
  rawUsername: string,
  hostId: string,
): Promise<void> {
  const handle = rawUsername.trim().replace(/^@/, '');
  if (!handle) throw new Error('Enter a username');
  const { data: prof, error: lookupError } = await supabase
    .from('profiles')
    .select('id')
    .eq('username', handle)
    .maybeSingle();
  if (lookupError) throw lookupError;
  if (!prof) throw new Error(`No member with username "${handle}"`);
  const { error } = await supabase
    .from('room_cohosts')
    .insert({ room_id: roomId, member_id: prof.id, designated_by: hostId });
  if (error) throw error;
}

export async function removeCohost(roomId: string, memberId: string): Promise<void> {
  const { error } = await supabase
    .from('room_cohosts')
    .delete()
    .eq('room_id', roomId)
    .eq('member_id', memberId);
  if (error) throw error;
}

/** Edit this broadcast's live ticker (host OR cohost). Column-safe via the
 *  set_room_ticker RPC, which authorizes host/cohost/admin and writes only
 *  ticker_lines. */
export async function updateRoomTicker(roomId: string, lines: string[]): Promise<void> {
  const { error } = await supabase.rpc('set_room_ticker', { p_room_id: roomId, p_lines: lines });
  if (error) throw error;
}

/** Host overrides a participant's on-stream name for all viewers (server op). */
export async function renameParticipant(
  roomId: string,
  identity: string,
  name: string,
): Promise<void> {
  const { error } = await supabase.functions.invoke('room-rename-participant', {
    body: { roomId, identity, name },
  });
  if (error) throw error;
}
